import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { AnnotationStatusBadge } from '../../../../modules/common/statusesBadges/annotationStatus/AnnotationStatusBadge';
import { EnableFor } from '../../../containers/EnableForRole';
import { useProjectDetailsPermissions } from '../../projectDetails/projectDetails.permissions';
import { ImageAnnotationStatus, ImageSetType } from '../../projectDetails/sub/images/projectDetailsImages.model';
import { AnnotationStatus } from '../annotations.interface';
import { ImageSetBadge } from './ImageSetBadge';

interface IAnnotationStatusInfoProps {
  isImageLoaded: boolean;
  annotationStatus: AnnotationStatus | undefined;
  isAssigned: boolean;
  annotatedByName: string;
  reviewedByName: string;
  reviewRejectionReason?: string;
  hasUnansweredClarification: boolean;
  isWaitingForReviewAfterReject: boolean;
  imageSet?: ImageSetType;
  enableImageSetChange: boolean;
  onImageSetChange: (imageSet: ImageSetType) => Promise<void>;
}

export const AnnotationStatusInfo = observer((props: IAnnotationStatusInfoProps): ReactElement => {
  const { t } = useTranslator();
  const {
    isImageLoaded,
    isAssigned,
    annotationStatus,
    annotatedByName,
    reviewedByName,
    reviewRejectionReason,
    isWaitingForReviewAfterReject,
    imageSet,
    onImageSetChange,
  } = props;

  const permissions = useProjectDetailsPermissions();
  const canChangeImageSet = permissions.canUpdateImageSet();
  const canSeeUsers = !!annotatedByName;

  return (
    <>
      {isImageLoaded && (
        <ColumnLayout
          size='md'
          className='annotation-status-info'
        >
          <div className='annotation-status-info-row'>
            <span className='annotation-status-info-row-title'>{t('status')}:</span>{' '}
            <AnnotationStatusBadge
              annotationStatus={props.hasUnansweredClarification ? ImageAnnotationStatus.CLARIFICATION : (annotationStatus ?? AnnotationStatus.NONE)}
            />
          </div>
          {imageSet && (
            <div className='annotation-status-info-row'>
              <span className='annotation-status-info-row-title'>{t('image_set_as')}:</span>{' '}
              <EnableFor hasPermission={canChangeImageSet}>
                <ImageSetBadge
                  set={imageSet}
                  disabled={!props.enableImageSetChange}
                  onChange={onImageSetChange}
                />
              </EnableFor>
            </div>
          )}
          {canSeeUsers && (
            <>
              {annotationStatus === AnnotationStatus.DRAFT && (
                <>
                  <div className='annotation-status-info-row'>
                    <span className='annotation-status-info-row-title'>{t('saved_by')}:</span> {annotatedByName}
                  </div>
                  {!isAssigned && <div>{t('unassigned')}</div>}
                </>
              )}
              {(annotationStatus === AnnotationStatus.TOREVIEW ||
                annotationStatus === AnnotationStatus.ACCEPTED ||
                annotationStatus === AnnotationStatus.REJECTED) && (
                <div className='annotation-status-info-row'>
                  <span className='annotation-status-info-row-title'>{t('annotated_by')}:</span> {annotatedByName}
                </div>
              )}
              {annotationStatus === AnnotationStatus.ACCEPTED && (
                <div className='annotation-status-info-row'>
                  <span className='annotation-status-info-row-title'>{t('accepted_by')}:</span> {reviewedByName}
                </div>
              )}
              {annotationStatus === AnnotationStatus.REJECTED && (
                <>
                  <div className='annotation-status-info-row'>
                    <span className='annotation-status-info-row-title'>{t('rejected_by')}:</span> {reviewedByName}
                  </div>
                </>
              )}
            </>
          )}
          {(annotationStatus === AnnotationStatus.REJECTED || isWaitingForReviewAfterReject) && (
            <div className='annotation-status-info-row'>
              <span className='annotation-status-info-row-title'>{t('reviewer_note')}:</span> <div className='rejection-reason'>{reviewRejectionReason}</div>
            </div>
          )}
        </ColumnLayout>
      )}
    </>
  );
});
